import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

// ============================================================================
// Types
// ============================================================================

export interface ReportedPost {
  id: string;
  post_id: string;
  reporter_id: string;
  reason: 'inappropriate' | 'harassment' | 'other';
  additional_info?: string;
  status: 'pending' | 'resolved' | 'dismissed';
  created_at: string;
  updated_at: string;
  post: {
    id: string;
    content: string;
    media_urls?: any;
    created_at: string;
    user_id: string;
    company_id: string;
    is_company_wide: boolean;
    author: {
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
    };
  };
  reporter: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
}

export interface ReportedComment {
  id: string;
  reporter_id: string;
  comment_id: string;
  post_id: string;
  reason: 'inappropriate' | 'harassment' | 'other';
  additional_info?: string;
  status: 'pending' | 'resolved' | 'dismissed';
  created_at: string;
  updated_at?: string;
  reporter: {
    id: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
  comment: {
    id: string;
    content: string;
    user_id: string;
    created_at: string;
    user: {
      id: string;
      first_name?: string;
      last_name?: string;
      avatar_url?: string;
    };
  };
  post: {
    id: string;
    content: string;
    user_id: string;
    created_at: string;
  };
}

export interface ReportedUser {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  reason: string;
  additional_info?: string;
  status: 'pending' | 'resolved' | 'dismissed';
  created_at: string;
  updated_at: string;
  reporter: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  reported_user: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
}

// ============================================================================
// Post Reports
// ============================================================================

export function useReportedPosts() {
  const { company } = useCurrentCompany();

  return useQuery({
    queryKey: ['admin', 'reported-posts', company?.id],
    enabled: !!company?.id,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ReportedPost[]> => {
      const { data, error } = await supabase
        .from('post_reports')
        .select(`
          *,
          post:posts!inner(
            id, content, media_urls, created_at, user_id, company_id, is_company_wide,
            author:user_profiles!inner(id, first_name, last_name, avatar_url)
          ),
          reporter:user_profiles!reporter_id(id, first_name, last_name, avatar_url)
        `)
        .eq('status', 'pending')
        .eq('post.company_id', company!.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ReportedPost[];
    },
  });
}

export function useApprovePostReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reportId }: { reportId: string }) => {
      const { error } = await supabase
        .from('post_reports')
        .update({ status: 'resolved', updated_at: new Date().toISOString() })
        .eq('id', reportId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reported-posts'] });
    },
  });
}

export function useDismissPostReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reportId }: { reportId: string }) => {
      const { error } = await supabase
        .from('post_reports')
        .update({ status: 'dismissed', updated_at: new Date().toISOString() })
        .eq('id', reportId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reported-posts'] });
    },
  });
}

export function useDeletePostAndWarn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      postId,
      userId,
      adminId,
      reason,
      warningType,
    }: {
      postId: string;
      userId: string;
      adminId: string;
      reason: string;
      warningType: 'inappropriate' | 'harassment' | 'other';
    }) => {
      const { error: warningError } = await supabase.rpc('apply_progressive_warning', {
        target_user_id: userId,
        admin_user_id: adminId,
        warning_reason: reason,
        warning_type: warningType,
        post_id: postId,
      });
      if (warningError) throw warningError;

      const { error: deleteError } = await supabase
        .from('posts')
        .update({ visible: false, updated_at: new Date().toISOString() })
        .eq('id', postId);
      if (deleteError) throw deleteError;

      const { error: reportError } = await supabase
        .from('post_reports')
        .update({ status: 'resolved', updated_at: new Date().toISOString() })
        .eq('post_id', postId);
      if (reportError) throw reportError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reported-posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['social'] });
    },
  });
}

// ============================================================================
// Comment Reports
// ============================================================================

export function useReportedComments() {
  const { company } = useCurrentCompany();

  return useQuery({
    queryKey: ['admin', 'reported-comments', company?.id],
    enabled: !!company?.id,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ReportedComment[]> => {
      // First get post IDs for this company to scope comment reports
      const { data: companyPosts } = await supabase
        .from('posts')
        .select('id')
        .eq('company_id', company!.id);

      const postIds = (companyPosts || []).map((p: any) => p.id);
      if (postIds.length === 0) return [];

      const { data, error } = await supabase
        .from('comment_reports')
        .select(`
          id, reporter_id, comment_id, post_id, reason, additional_info, status, created_at, updated_at,
          reporter:reporter_id(id, first_name, last_name, avatar_url),
          comment:comment_id(id, content, user_id, created_at, user:user_id(id, first_name, last_name, avatar_url)),
          post:post_id(id, content, user_id, created_at)
        `)
        .eq('status', 'pending')
        .in('post_id', postIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((report: any) => ({
        ...report,
        reporter: report.reporter || { id: report.reporter_id },
        comment: report.comment || { id: report.comment_id, content: '', user_id: '', created_at: '', user: { id: '' } },
        post: report.post || { id: report.post_id, content: '', user_id: '', created_at: '' },
      }));
    },
  });
}

export function useDismissCommentReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reportId }: { reportId: string }) => {
      const { error } = await supabase
        .from('comment_reports')
        .update({ status: 'dismissed', updated_at: new Date().toISOString() })
        .eq('id', reportId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reported-comments'] });
    },
  });
}

export function useDeleteCommentAndWarn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      commentId,
      userId,
      adminId,
      reason,
      warningType,
      reportId,
    }: {
      commentId: string;
      userId: string;
      adminId: string;
      reason: string;
      warningType: 'inappropriate' | 'harassment' | 'other';
      reportId: string;
    }) => {
      const { error: warningError } = await supabase.rpc('apply_progressive_warning', {
        target_user_id: userId,
        admin_user_id: adminId,
        warning_reason: reason,
        warning_type: warningType,
      } as any);
      if (warningError) throw warningError;

      const { error: deleteError } = await supabase.from('comments').delete().eq('id', commentId);
      if (deleteError) throw deleteError;

      const { error: reportError } = await supabase
        .from('comment_reports')
        .update({ status: 'resolved', updated_at: new Date().toISOString() })
        .eq('id', reportId);
      if (reportError) throw reportError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reported-comments'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['social'] });
    },
  });
}

// ============================================================================
// User Reports
// ============================================================================

export function useReportedUsers() {
  return useQuery({
    queryKey: ['admin', 'reported-users'],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ReportedUser[]> => {
      const { data, error } = await supabase
        .from('user_reports')
        .select(`
          *,
          reporter:user_profiles!reporter_id(id, first_name, last_name, avatar_url),
          reported_user:user_profiles!reported_user_id(id, first_name, last_name, avatar_url)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ReportedUser[];
    },
  });
}

export function useDismissUserReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reportId }: { reportId: string }) => {
      const { error } = await supabase
        .from('user_reports')
        .update({ status: 'dismissed', updated_at: new Date().toISOString() })
        .eq('id', reportId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reported-users'] });
    },
  });
}

export function useWarnUserAndResolve() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      adminId,
      reason,
      warningType,
      reportId,
    }: {
      userId: string;
      adminId: string;
      reason: string;
      warningType: 'inappropriate' | 'harassment' | 'other';
      reportId: string;
    }) => {
      const { error: warningError } = await supabase.rpc('apply_progressive_warning', {
        target_user_id: userId,
        admin_user_id: adminId,
        warning_reason: reason,
        warning_type: warningType,
      });
      if (warningError) throw warningError;

      const { error: reportError } = await supabase
        .from('user_reports')
        .update({ status: 'resolved', updated_at: new Date().toISOString() })
        .eq('id', reportId);
      if (reportError) throw reportError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reported-users'] });
    },
  });
}
